/* eslint-disable @typescript-eslint/no-explicit-any */
import { useId, useMemo, useRef, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { ingestFiles } from "@/lib/file-ingest";
import { PROJECT_DOCUMENT_ACCEPT, MAX_SHARE_ATTACHMENT_BYTES } from "@/lib/upload-limits";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useQuery } from "@apollo/client/react";
import { Tooltip } from "@/components/common/Tooltip";
import { FileDropZone, type FileDropZoneHandle } from "@/components/common/FileDropZone";
import { Button } from "@/components/design/Button";
import { Collapse } from "@/components/design/Collapse";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { Disclosure } from "@/components/design/Disclosure";
import { NumberStepper } from "@/components/design/NumberStepper";
import { Select } from "@/components/design/Select";
import { MatchTeamProfilesDocument } from "@/graphql/generated/operations";
import {
  UnlinkConfirmModal,
  CreateProjectModal,
  VerknuepfenModal,
} from "./MatchModals";
import {
  DocumentPickerDialog,
  type PickedDocument,
} from "@/components/share/DocumentPickerDialog";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { AgentPicker } from "./AgentPicker";
import { PowerProfileEditor } from "./PowerProfileEditor";
import { TeamContextPanel } from "./TeamContextPanel";
import { PowerScoreSettings } from "./PowerScoreSettings";
import { SubsectionHeader } from "./requirements/row-primitives";
import { CandidateAssessmentPromptEditor } from "./CandidateAssessmentPromptEditor";
import { RequirementsPanel as TypedRequirementsPanel } from "./requirements/RequirementsPanel";
import { CompanyRequirementsPanel } from "./requirements/CompanyRequirementsPanel";
import { ExpertiseSection } from "./requirements/ExpertiseSection";
import { RecruitingExperience } from "./requirements/RecruitingExperience";
import { RecruitingQualifications } from "./requirements/RecruitingQualifications";
import { DetailsSection, DETAIL_TYPES } from "./requirements/DetailsSection";
import type { FileEntry, MatchData, SlideId } from "./match-design-types";
import type { LinkedSharedItem, MatchRole, UseMatchEditorResult } from "./use-match-editor";
import { Link, useNavigate } from "react-router";
import { BALANCED_TEAM_PROFILE_SEED_ID, DEFAULT_TEAM_PROFILE_ID, LEGACY_BALANCED_TEAM_PROFILE_ID, POWERS_WEIGHT_MIN, TEAM_SIZE_MAX, canPublishMatch, densifyElementWeights, makeIndividualTeamProfile, normalizePowersWeight } from "./match-design-rules";
import {
  describesRole,
  matchSubject,
  ranksCandidates,
  resolveMatchMode,
  showsTeamControls,
  usesPowerScoreSettings,
} from "./match-mode";

export function ChallengeStep({
  data,
  update,
  editor,
  sharedItem,
  recruiting = false,
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  recruiting?: boolean;
  editor: Pick<
    UseMatchEditorResult,
    // The generation trigger awaits flush() so the assistant proposes against the brief and
    // documents the server actually has, not the ones still sitting in the autosave debounce
    // (smycles/development/services/agent-service#95). `saveState` closes the other half: flush()
    // only guards a *concurrent* failure.
    | "flush"
    | "saveState"
    | "actions"
    | "linkSharedItem"
    | "unlinkSharedItem"
    | "createProjectFromMatch"
    | "attachProjectDocuments"
    | "removeProjectDocument"
  >;
  sharedItem: LinkedSharedItem;
  myRole: MatchRole | null;
  onNavigate: (slide: SlideId) => void;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const [publishOpen, setPublishOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [serviceImportOpen, setServiceImportOpen] = useState(false);
  const [serviceImportError, setServiceImportError] = useState<string | null>(null);

  const canEditContent = editor.actions?.canUpdateDesign === true;
  const canManageSharedItem = editor.actions?.canLinkSharedItem === true;
  // A project exists once it has a name (genesis Project), so the title is what unlocks it.
  const canPublish = canEditContent && canPublishMatch(data);
  const documentRequirements = [
    { label: intl.formatMessage({ id: "match.docs.requiredTitle", defaultMessage: "Titel" }), met: data.projectName.trim().length > 0 },
    { label: intl.formatMessage({ id: "match.docs.requiredDescription", defaultMessage: "Beschreibung" }), met: data.projectDescription.trim().length > 0 },
  ];
  const hasProjectBrief = documentRequirements.every((requirement) => requirement.met);
  const canGenerate = canEditContent && hasProjectBrief;
  // ONE precondition for EVERY assistant hand-off on this step, so the interview and the generation
  // trigger cannot drift apart again (the interview shipped without it and ran against stale data).
  // After an autosave has ALREADY failed, flush() resolves silently — performSave nulled inFlight in
  // its finally and the timeout nulled the debounce, so both of flushAutosave's guards are false —
  // and the assistant would work against a server state the user is not looking at. Gating the
  // triggers is the scoped fix; the root cause is that flush() cannot reject on an ALREADY-failed
  // save (it rejects fine on a concurrent one), and teaching it to would turn runSolver's four
  // `void editor.runSolver()` callers into unhandled rejections.
  const assistantBlocked = editor.saveState === "error";
  // Rank describes a role / open position with per-candidate requirements, not a team
  // project (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4) — the left-panel framing changes accordingly. Optimize is the only
  // mode with a team size for the AI to derive. Both read as intents off the resolved mode:
  // `!== "ASSESS" && !== "RANK"` used to mean Optimize here, so any mode this build did not
  // know got Optimize's team controls (see match-mode.ts).
  const mode = resolveMatchMode(data.matchMode);
  const roleFramed = describesRole(mode);
  const teamControls = showsTeamControls(mode);
  // A company match authors COMPANY criteria, and only those. Everything the person branch puts in
  // this card is person-shaped: the requirement editors write MatchCondition rows genesis' company
  // arm never reads, and the Power Scorecard is a weight vector over people. So the whole right-hand
  // stack forks, not a control here and there.
  //
  // ONE EXCEPTION SINCE CONCEPT v17 (b): the "Bewertungsstil" box now exists in BOTH shapes. Its
  // person form supplements the RECRUITER prompt for the per-candidate narrative, which a company
  // run never reaches; its company form (CompanyAssessmentStyle) carries only the switch, because
  // the one thing a company run does ask the model is whether to judge the expertise wording.
  const companyCriteria = matchSubject(mode) === "companies";

  // Assistant frame (view-state only, no PII): mirror what the Challenge step shows. Team size is
  // Optimize-only (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4: Assess/Rank have no team to size), so it's omitted in those modes.
  const authoredCriteria = companyCriteria ? (data.companyConditions ?? []) : data.conditions;
  const mustCount = authoredCriteria.filter((c) => c.requirement === "MUST").length;
  useAssistantContext([
    {
      description: "the match requirements the user is editing",
      value:
        `${authoredCriteria.length} requirements (${mustCount} MUSS / ${authoredCriteria.length - mustCount} KANN)` +
        (teamControls ? `; team size ${data.teamSize}` : "") +
        `; ${data.projectDescription.trim() ? "project brief written" : "no project brief yet"}`,
    },
    ...(roleFramed && data.recruitingTeamFit ? [{
      description: "the existing team used for automatic recruiting Team-Fit",
      value: `${data.recruitingTeamFit.members.length} existing members; team Power Scorecard: ${data.recruitingTeamFit.scorecard.name}`,
    }] : []),
  ]);
  // KI-Signal: die Bewertungsstil-Box traegt den Signal-Schatten selbst in allen Modi und in
  // BEIDEN Armen (data.aiEnabled, siehe CandidateAssessmentPromptEditor). Zusaetzlich tragen im Rank
  // die Rollenanforderungen den Schatten, weil sie bei aktiver KI Modell-Input sind
  // (UI_CONVENTIONS, Where AI lives). Team Powers bleibt ohne Signal (deterministischer
  // Power-Fit). Offen (spark #233): die Assess/Optimize-Teamanforderungen tragen den
  // Sektions-Schatten noch nicht.
  // transition in beiden Zustaenden, damit der Schatten beim Umschalten ein- UND
  // ausblendet statt hart zu wechseln.
  const aiSectionSignal = ranksCandidates(mode)
    ? `transition ${data.aiEnabled ? "smycles-ai-shadow" : ""}`
    : "";

  const resultRef = useRef<HTMLDivElement>(null);

  async function addPickedFromService(picked: PickedDocument[]) {
    if (!canEditContent || !hasProjectBrief) return;
    setServiceImportError(null);
    // base64 length * 3/4 ≈ raw byte size; same heuristic ShareCreatePage uses.
    const rawBytes = (p: PickedDocument) => Math.floor(p.content.length * 0.75);
    const tooBig = picked.filter((p) => rawBytes(p) > MAX_SHARE_ATTACHMENT_BYTES);
    if (tooBig.length > 0) {
      const maxMb = Math.floor(MAX_SHARE_ATTACHMENT_BYTES / (1024 * 1024));
      setServiceImportError(
        intl.formatMessage(
          { id: "match.docs.tooLarge", defaultMessage: "Zu groß (max. {max} MB pro Datei): {names}" },
          { max: maxMb, names: tooBig.map((p) => p.name).join(", ") },
        ),
      );
    }
    const ok = picked.filter((p) => rawBytes(p) <= MAX_SHARE_ATTACHMENT_BYTES);
    if (ok.length === 0) return;
    try {
      await editor.attachProjectDocuments(
        ok.map((p) => ({ fileName: p.name, mimeType: p.mimeType, content: p.content })),
      );
    } catch (err) {
      setServiceImportError(userErrorMessage(intl, err));
    }
  }

  const interviewHandoff = (
    <AskAssistantButton
      variant="ghost"
      prompt={intl.formatMessage({
        id: "match.challenge.interview.seed",
        defaultMessage: "Interviewe mich zu diesem Projekt, damit wir eine gute Projektbeschreibung bekommen.",
      })}
      // Same flush as the generate button: the interview reads the brief from genesis, so without it
      // the assistant interviews against the description as it was before the user's last
      // keystrokes — the debounce has not fired yet.
      onBeforeAsk={editor.flush}
      disabled={assistantBlocked}
      label={data.projectDescription.trim().length === 0 ? (
        <FormattedMessage id="match.challenge.interview.invite" defaultMessage="Unsicher, was rein soll? Lass dich vom Assistenten interviewen." />
      ) : (
        <FormattedMessage id="match.challenge.interview.refine" defaultMessage="Mit einem Interview verfeinern" />
      )}
    />
  );

  return (
    <div>
      <div className="grid grid-cols-1 items-start gap-6 @lg:grid-cols-2">
        <div className="box bg-surface p-6 pt-7">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            {/* Ein Titel fuer alle Modi (spark #1073): die Spalte traegt ueberall dasselbe. Was
                sich je Modus unterscheidet, sind die Felder darin — Rollen- vs. Projekt-Titel. */}
            <span className="text-smycles-label tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="match.challenge.matchContext" defaultMessage="Match-Kontext" />
            </span>
            {canManageSharedItem && !sharedItem && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  size="compact"
                  className="gap-1.5 whitespace-nowrap"
                  onClick={() => setLinkOpen(true)}
                  title={intl.formatMessage({ id: "match.challenge.linkProjectTitle", defaultMessage: "Projekt mit existierendem Share-Item verknüpfen" })}
                >
                  <span>↘</span> <FormattedMessage id="match.challenge.linkBtn" defaultMessage="Verknüpfen" />
                </Button>
                <Button
                  variant="secondary"
                  size="compact"
                  className="gap-1.5 whitespace-nowrap"
                  id="match-create-project"
                  onClick={() => setPublishOpen(true)}
                  disabled={!canPublish}
                  title={
                    canPublish
                      ? intl.formatMessage({ id: "match.createProject", defaultMessage: "Als Projekt anlegen" })
                      : intl.formatMessage({ id: "match.challenge.enterTitle", defaultMessage: "Titel eintragen" })
                  }
                >
                  <span>↗</span> <FormattedMessage id="match.challenge.publishBtn" defaultMessage="Als Projekt anlegen" />
                </Button>
              </div>
            )}
            {sharedItem && (
              <div className="flex flex-col items-end gap-0.5 text-right text-smycles-micro text-ink-muted">
                <Link
                  to={`/share/discover/${sharedItem.id}`}
                  className="underline-offset-2 hover:text-ink hover:underline"
                  title={intl.formatMessage({ id: "match.challenge.openLinkedShare", defaultMessage: "Verknüpftes Share-Item öffnen" })}
                >
                  ↗ {sharedItem.name || intl.formatMessage({ id: "match.challenge.unnamedSharedItem", defaultMessage: "Unbenanntes Share-Item" })}
                </Link>
                {canManageSharedItem && (
                  <button
                    onClick={() => setUnlinkOpen(true)}
                    className="text-ink-muted underline-offset-2 transition hover:text-ink-accent hover:underline"
                    title={intl.formatMessage({ id: "match.challenge.unlinkTitle", defaultMessage: "Verbindung zum Share-Item lösen" })}
                  >
                    <FormattedMessage id="match.challenge.unlink" defaultMessage="Verbindung lösen" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2">
              <div className="bg-page box-flat p-5 transition focus-within:ring-1 focus-within:ring-ink-accent-ui">
                <div className="smycles-field-label mb-1">
                  {roleFramed ? (
                    <FormattedMessage id="match.challenge.roleTitleLabel" defaultMessage="Rollen-Titel" />
                  ) : (
                    <FormattedMessage id="match.challenge.projectTitleLabel" defaultMessage="Projekt-Titel" />
                  )}
                </div>
                <input
                  id="match-project-title"
                  value={data.projectName}
                  onChange={(e) => update({ projectName: e.target.value })}
                  readOnly={!canEditContent}
                  placeholder={intl.formatMessage({ id: "match.titlePlaceholder", defaultMessage: "z. B. Set-Crew BTL · Q2 2026" })}
                  className="w-full bg-transparent text-smycles-title font-medium outline-none placeholder:text-ink-muted read-only:text-ink-muted"
                />
              </div>

              <div className="bg-page box-flat p-5 transition focus-within:ring-1 focus-within:ring-ink-accent-ui">
                <div className="smycles-field-label mb-1">
                  <FormattedMessage id="match.fieldShortDesc" defaultMessage="Kurzbeschreibung" />
                </div>
                <input
                  id="match-short-description"
                  value={data.projectShortDescription}
                  onChange={(e) => update({ projectShortDescription: e.target.value })}
                  readOnly={!canEditContent}
                  placeholder={intl.formatMessage({ id: "match.shortDescPlaceholder", defaultMessage: "In einem Satz: was, wofür?" })}
                  className="w-full bg-transparent text-smycles-body outline-none placeholder:text-ink-muted read-only:text-ink-muted"
                />
              </div>
            </div>

            <div className="bg-page box-flat p-5 transition focus-within:ring-1 focus-within:ring-ink-accent-ui">
              <div className="smycles-field-label mb-1">
                <FormattedMessage id="match.fieldDetailedDesc" defaultMessage="Detaillierte Beschreibung" />
              </div>
              <textarea
                id="match-detailed-description"
                value={data.projectDescription}
                onChange={(e) => update({ projectDescription: e.target.value })}
                readOnly={!canEditContent}
                placeholder={intl.formatMessage({ id: "match.detailedPlaceholder", defaultMessage: "Kontext, Outcome, Constraints, Vorerfahrungen, alles was sonst relevant ist." })}
                rows={4}
                className="field-sizing-content w-full resize-none bg-transparent text-smycles-body leading-relaxed outline-none placeholder:text-ink-muted read-only:text-ink-muted"
              />
              <div className="mt-2 flex items-center justify-between gap-3">
                {!canEditContent ? (
                  <span />
                ) : assistantBlocked ? (
                  // Same reasoning as the generation trigger's tooltip: the real cause sits in the
                  // SaveStatus bar up in the header, and nothing on this row connects the two.
                  <Tooltip
                    placement="right"
                    content={intl.formatMessage({ id: "match.save.failed", defaultMessage: "Speichern fehlgeschlagen" })}
                  >
                    {interviewHandoff}
                  </Tooltip>
                ) : (
                  interviewHandoff
                )}
                <span
                  id="match-description-char-count"
                  className="text-smycles-micro tracking-wider text-ink-muted tabular-nums"
                >
                  <FormattedMessage id="match.charsCount" defaultMessage="{count} Zeichen" values={{ count: data.projectDescription.length }} />
                </span>
              </div>
            </div>

            {roleFramed && <TeamContextPanel data={data} update={update} readOnly={!canEditContent} />}

            <AgentPicker
              agents={data.agents}
              projectText={`${data.projectName} ${data.projectShortDescription} ${data.projectDescription}`}
              enabled={data.agenticAi}
              onEnabledChange={(next) => update({ agenticAi: next })}
              onChange={(agents) => update({ agents })}
              readOnly={!canEditContent}
            />

            <ProjectDocumentDropZone
              label={intl.formatMessage({ id: "match.documents", defaultMessage: "Dokumente" })}
              files={data.projectFiles}
              attachProjectDocuments={editor.attachProjectDocuments}
              removeProjectDocument={editor.removeProjectDocument}
              readOnly={!canEditContent}
              uploadRequirements={documentRequirements}
              extraAction={
                canEditContent ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!hasProjectBrief}
                    onClick={() => {
                      setServiceImportError(null);
                      setServiceImportOpen(true);
                    }}
                  >
                    <FormattedMessage id="share.create.fromDocuments" defaultMessage="From my documents" />
                  </Button>
                ) : undefined
              }
            />
            {serviceImportError ? (
              <div className="text-smycles-meta text-red-600">{serviceImportError}</div>
            ) : null}
          </div>

        </div>

        <div ref={resultRef} className="box scroll-mt-6 bg-surface p-6 pt-7">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-smycles-label tracking-[0.25em] text-ink-muted">
                <FormattedMessage id="match.challenge.requirements" defaultMessage="Anforderungen" />
              </div>
              {/* Hinweis als leise Bildunterschrift zum Titel statt eigener Box:
                  qualifiziert den ganzen Abschnitt, ohne Alarm-Gewicht. */}
              {/* Beide Saetze sind Aussagen ueber die Datenquelle, und die Quelle unterscheidet
                  sich: Personen-Kriterien lesen nur geteilte Profilinformationen, Unternehmens-Kriterien
                  lesen die Unternehmens-Zeile (industry_id, employee_count). Der Personen-Satz waere
                  hier also nicht bloss unpassend, sondern falsch. */}
              <p className="mt-1.5 smycles-field-meta max-w-2xl text-ink-muted">
                {companyCriteria ? (
                  <FormattedMessage
                    id="match.challenge.requirementsCompanyInfo"
                    defaultMessage="Bewertet werden die Angaben aus dem Unternehmensprofil."
                  />
                ) : (
                  <FormattedMessage
                    id="match.challenge.requirementsSharedInfo"
                    defaultMessage="Berücksichtigt werden nur Profilinformationen, die Kandidat:innen geteilt haben."
                  />
                )}
              </p>
            </div>
            {canEditContent && (
              <Tooltip
                placement="left"
                // Every branch of `disabled` below needs its own reason here, or the control is
                // silently dead: the real cause of the save-error branch sits in the SaveStatus bar
                // up in the header, and nothing on this row connects the two.
                content={
                  assistantBlocked
                    ? intl.formatMessage({ id: "match.save.failed", defaultMessage: "Speichern fehlgeschlagen" })
                    : canGenerate
                      ? intl.formatMessage({ id: "match.challenge.aiDeriveHint", defaultMessage: "Öffnet den Assistenten, der Anforderungen aus deiner Beschreibung vorschlägt." })
                      : intl.formatMessage({ id: "match.challenge.aiBriefRequiredHint", defaultMessage: "Erst Titel und Beschreibung ausfüllen." })
                }
              >
                <AskAssistantButton
                  prompt={companyCriteria ? intl.formatMessage({
                    id: "match.challenge.generateCompanyRequirementsPrompt",
                    defaultMessage: "Schlag mir passende Unternehmens-Anforderungen (Branche, Unternehmensgröße und Fachwissen) aus Titel, Kurz- und Detailbeschreibung vor.",
                  }) : intl.formatMessage({
                    id: "match.challenge.generateRequirementsPrompt",
                    defaultMessage: "Schlag mir passende Anforderungen aus Titel, Kurz- und Detailbeschreibung vor.",
                  })}
                  // Flush first (pending autosave + in-flight project-document mutations), then send
                  // the intent. A rejected flush cancels the ask — see AskAssistantButton.
                  onBeforeAsk={editor.flush}
                  // The typed intent, person arm only: agent-service's proposal turn writes
                  // MatchCondition rows, which the company arm never reads. Company generation keeps
                  // running off the prompt wording until it gets an action of its own.
                  action={companyCriteria ? undefined : "GENERATE_MATCH_REQUIREMENTS"}
                  label={<FormattedMessage id="match.challenge.generateWithAi" defaultMessage="Für mich generieren" />}
                  disabled={!canGenerate || assistantBlocked}
                />
              </Tooltip>
            )}
          </div>
          <div className="space-y-3">
            {/* EINE BOX FUER BEIDE ARME. Der Unternehmens-Arm hatte hier bis spark #294 eine Attrappe:
                ein fest eingeschalteter, nicht bedienbarer Schalter, waehrend die Verdrahtung in
                Wahrheit `agenticAi` las. Die Box entscheidet jetzt selbst anhand des Modus, was
                sie zeigt — im Unternehmens-Arm die flache Zeile mit dem Schalter, sonst die Disclosure
                mit den Reglern. */}
            <CandidateAssessmentPromptEditor
              data={data}
              update={update}
              readOnly={!canEditContent}
              mode={mode}
            />
            {companyCriteria ? (
              // Company ranking branches before the person power fetch and has no team profile or
              // power result, so the lens has nothing to select in this mode.
              <CompanyRequirementsPanel data={data} update={update} readOnly={!canEditContent} />
            ) : recruiting ? (
              <>
                {/* Rank spiegelt das Optimize/Assess-Layout: alle Rollenanforderungen als
                    Untersektionen in EINER Karte, Fachwissen zuoberst (meist wichtigstes Kriterium).
                    Team Powers bleibt eine eigene Karte, wie in den anderen Modi (spark #230). */}
                <Disclosure
                  className={aiSectionSignal}
                  title={intl.formatMessage({
                    id: "match.roleReqsSection",
                    defaultMessage: "Rollenanforderungen",
                  })}
                  defaultOpen
                >
                  <div className="space-y-6">
                    <ExpertiseSection data={data} update={update} readOnly={!canEditContent} />
                    <DetailsSection
                      conditions={data.conditions.filter((c) => DETAIL_TYPES.has(c.__typename))}
                      onChange={(next) =>
                        update({
                          conditions: [
                            ...data.conditions.filter((c) => !DETAIL_TYPES.has(c.__typename)),
                            ...next,
                          ],
                        })
                      }
                      showRequirement
                      singlePerson
                      readOnly={!canEditContent}
                    />
                    <RecruitingExperience data={data} update={update} readOnly={!canEditContent} />
                    <RecruitingQualifications data={data} update={update} readOnly={!canEditContent} />
                  </div>
                </Disclosure>
                {/* Rank/Bewerbung: Powers als kandidaten-absolute Wuensche (Wire 0-1000, kein Muss/Kann-Gate),
                    speist den Power-Fit als zweite Achse im Ergebnis (spark #151, @adr SPARK-0030-match-result-scoring-atom-derived@1). */}
                <TeamPowersPanel data={data} update={update} readOnly={!canEditContent} />
              </>
            ) : (
              <>
                <RequirementsPanel data={data} update={update} readOnly={!canEditContent} />
                <TeamPowersPanel data={data} update={update} readOnly={!canEditContent} />
              </>
            )}
          </div>
        </div>
      </div>

      {linkOpen && (
        <VerknuepfenModal
          data={data}
          canCopy={canEditContent}
          onConfirm={async ({ sharedItemId, copy }) => {
            await editor.linkSharedItem({ sharedItemId, copy });
          }}
          onClose={() => setLinkOpen(false)}
        />
      )}

      {unlinkOpen && sharedItem && (
        <UnlinkConfirmModal
          sharedItemName={sharedItem.name || intl.formatMessage({ id: "match.challenge.unnamedSharedItem", defaultMessage: "Unbenanntes Share-Item" })}
          onConfirm={async () => {
            await editor.unlinkSharedItem();
          }}
          onClose={() => setUnlinkOpen(false)}
        />
      )}

      {publishOpen && (
        <CreateProjectModal
          projectName={data.projectName}
          onCreate={editor.createProjectFromMatch}
          onOpen={(projectId) => navigate(`/project/${projectId}`)}
          onClose={() => setPublishOpen(false)}
        />
      )}

      <DocumentPickerDialog
        open={serviceImportOpen && canEditContent && hasProjectBrief}
        kind="file"
        onClose={() => setServiceImportOpen(false)}
        onPick={(picked) => void addPickedFromService(picked)}
      />

    </div>
  );
}

// One file in the dropzone — either confirmed server-side (has backdropFileId,
// rendered as `state: "ready"`) or in-flight on the client (has clientId,
// `state: "uploading" | "error"`). Server entries can be detached; client
// entries can be retried (error) or dismissed (drops local state).
interface ProjectDocumentRow {
  key: string;
  name: string;
  size: number;
  state: "ready" | "uploading" | "error";
  errorMessage?: string;
  // ready → detach via genesis; error/uploading → drop the local pending entry.
  onRemove?: () => void;
  onRetry?: () => void;
}

interface PendingUpload {
  clientId: string;
  fileName: string;
  size: number;
  mimeType: string;
  state: "uploading" | "error";
  errorMessage?: string;
  // Retained while in-flight or in error so a retry doesn't re-read the disk.
  payload: { fileName: string; mimeType: string; content: string };
}

function ProjectDocumentDropZone({
  label,
  files,
  attachProjectDocuments,
  removeProjectDocument,
  extraAction,
  readOnly,
  uploadRequirements,
}: {
  label: string;
  files: FileEntry[];
  attachProjectDocuments: (
    files: Array<{ fileName: string; mimeType: string; content: string }>,
  ) => Promise<void>;
  removeProjectDocument: (backdropFileId: string) => Promise<void>;
  extraAction?: ReactNode;
  readOnly: boolean;
  uploadRequirements: ReadonlyArray<{ label: string; met: boolean }>;
}) {
  const intl = useIntl();
  const uploadHintId = useId();
  const missingRequirements = uploadRequirements.filter((requirement) => !requirement.met);
  const canUpload = !readOnly && missingRequirements.length === 0;
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const dropZoneRef = useRef<FileDropZoneHandle>(null);

  async function ingest(fileList: FileList | null) {
    if (!canUpload) return;
    let result;
    try {
      result = await ingestFiles(fileList, { maxBytes: MAX_SHARE_ATTACHMENT_BYTES });
    } catch (err) {
      setTopLevelError(userErrorMessage(intl, err));
      return;
    }

    if (result.rejected.length > 0) {
      const maxMb = Math.floor(MAX_SHARE_ATTACHMENT_BYTES / (1024 * 1024));
      setTopLevelError(
        intl.formatMessage(
          {
            id: "match.docs.tooLarge",
            defaultMessage: "Zu groß (max. {max} MB pro Datei): {names}",
          },
          { max: maxMb, names: result.rejected.map((r) => r.file.name).join(", ") },
        ),
      );
    } else {
      setTopLevelError(null);
    }
    if (result.accepted.length === 0) return;

    const payloads = result.accepted.map((f) => ({
      fileName: f.name,
      mimeType: f.mimeType,
      content: f.content,
    }));
    const created: PendingUpload[] = result.accepted.map((f, i) => ({
      clientId: crypto.randomUUID(),
      fileName: f.name,
      size: f.size,
      mimeType: f.mimeType,
      state: "uploading",
      payload: payloads[i]!,
    }));
    const createdIds = new Set(created.map((c) => c.clientId));
    setPendingUploads((prev) => [...prev, ...created]);

    try {
      await attachProjectDocuments(payloads);
      // Success: genesis returned the updated Match; commitServerTruth's
      // refetch updates `files` (server-side) with the new entries. Drop
      // the local pending rows so they don't double-render alongside.
      setPendingUploads((prev) => prev.filter((p) => !createdIds.has(p.clientId)));
    } catch (err) {
      const msg = userErrorMessage(intl, err);
      setPendingUploads((prev) =>
        prev.map((p) =>
          createdIds.has(p.clientId)
            ? { ...p, state: "error" as const, errorMessage: msg }
            : p,
        ),
      );
    }
  }

  async function retry(clientId: string) {
    if (!canUpload) return;
    const entry = pendingUploads.find((p) => p.clientId === clientId);
    if (!entry) return;
    setPendingUploads((prev) =>
      prev.map((p) =>
        p.clientId === clientId
          ? { ...p, state: "uploading" as const, errorMessage: undefined }
          : p,
      ),
    );
    try {
      await attachProjectDocuments([entry.payload]);
      setPendingUploads((prev) => prev.filter((p) => p.clientId !== clientId));
    } catch (err) {
      const msg = userErrorMessage(intl, err);
      setPendingUploads((prev) =>
        prev.map((p) =>
          p.clientId === clientId
            ? { ...p, state: "error" as const, errorMessage: msg }
            : p,
        ),
      );
    }
  }

  function dismissPending(clientId: string) {
    setPendingUploads((prev) => prev.filter((p) => p.clientId !== clientId));
  }

  async function detachServerFile(backdropFileId: string) {
    try {
      await removeProjectDocument(backdropFileId);
    } catch (err) {
      setTopLevelError(userErrorMessage(intl, err));
    }
  }

  // Merged display: server-confirmed entries first (stable across renders;
  // their backdropFileId is the key), then in-flight client entries.
  const rows: ProjectDocumentRow[] = [
    ...files.map<ProjectDocumentRow>((f) => ({
      key: f.backdropFileId ?? `noid:${f.name}`,
      name: f.name,
      size: f.size,
      state: "ready",
      onRemove:
        !readOnly && f.backdropFileId
          ? () => void detachServerFile(f.backdropFileId!)
          : undefined,
    })),
    ...pendingUploads.map<ProjectDocumentRow>((p) => ({
      key: `pending:${p.clientId}`,
      name: p.fileName,
      size: p.size,
      state: p.state,
      errorMessage: p.errorMessage,
      onRemove: !readOnly ? () => dismissPending(p.clientId) : undefined,
      onRetry: canUpload && p.state === "error" ? () => void retry(p.clientId) : undefined,
    })),
  ];

  const fileGrid = rows.length > 0 ? (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      {rows.map((row) => <ProjectDocumentTile key={row.key} row={row} />)}
    </div>
  ) : undefined;

  return (
    <div className="bg-page box-flat p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">{label}</div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {extraAction}
          {!readOnly && rows.length > 0 && (
            <Button variant="secondary" size="sm" disabled={!canUpload} onClick={() => dropZoneRef.current?.openPicker()}>
              <FormattedMessage id="service.page.uploadFile" defaultMessage="Upload file" />
            </Button>
          )}
        </div>
      </div>

      {!readOnly ? (
        <FileDropZone
          ref={dropZoneRef}
          variant={canUpload && rows.length === 0 ? "zone" : "overlay"}
          clickToOpen={rows.length === 0}
          disabled={!canUpload}
          onFiles={(files) => void ingest(files)}
          accept={PROJECT_DOCUMENT_ACCEPT}
          multiple
          className="mt-2 rounded-md"
          ariaLabel={intl.formatMessage({ id: "match.addDocumentsAriaLabel", defaultMessage: "Dokumente hinzufügen" })}
          ariaDescribedBy={!canUpload ? uploadHintId : undefined}
          label={intl.formatMessage({ id: "service.page.noDocuments", defaultMessage: "No documents" })}
          hint={intl.formatMessage({ id: "service.page.emptyDropHint", defaultMessage: "Drag files here or click to upload" })}
        >
          {!canUpload ? (
            <>
              <div id={uploadHintId} className="py-4 text-left text-smycles-meta text-ink-muted">
                <FormattedMessage
                  id="match.docs.stillNeeded"
                  defaultMessage="Noch benötigt: {requirements}"
                  values={{ requirements: missingRequirements.map((requirement) => requirement.label).join(" & ") }}
                />
              </div>
              {fileGrid}
            </>
          ) : fileGrid}
        </FileDropZone>
      ) : (
        <div className="mt-2">
          {fileGrid ?? (
            <div className="py-6 text-center text-smycles-meta text-ink-muted">
              <FormattedMessage id="match.challenge.noFilesYet" defaultMessage="Noch keine Dateien." />
            </div>
          )}
        </div>
      )}

      {topLevelError && (
        <div role="alert" className="mt-2 text-smycles-label text-ink-accent">
          {topLevelError}
        </div>
      )}
    </div>
  );
}

function ProjectDocumentTile({ row }: { row: ProjectDocumentRow }) {
  const intl = useIntl();
  const kb = row.size ? Math.round(row.size / 1024) : 0;
  const isUploading = row.state === "uploading";
  const isError = row.state === "error";
  return (
    <div
      className={`bg-page box-flat group relative overflow-hidden ${
        isError ? "ring-1 ring-accent-decoration" : ""
      }`}
    >
      {row.onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            row.onRemove?.();
          }}
          className="absolute right-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-smycles-micro text-ink opacity-0 transition group-hover:opacity-100"
          title={intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })}
        >
          ✕
        </button>
      )}
      <div className="relative flex aspect-[4/3] items-center justify-center text-smycles-display-s text-ink-muted">
        📄
        {isUploading && (
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center bg-page/70"
          >
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent text-ink-muted" />
          </span>
        )}
        {isError && (
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center bg-page/70 text-smycles-title text-ink-accent"
          >
            ⚠
          </span>
        )}
      </div>
      <div className="px-2 py-1.5">
        <div className="truncate text-smycles-label font-medium">{row.name}</div>
        <div className="text-smycles-micro text-ink-muted">
          {isUploading ? (
            <FormattedMessage id="match.docs.uploading" defaultMessage="Wird hochgeladen…" />
          ) : isError ? (
            <span className="text-ink-accent">
              {row.errorMessage ??
                intl.formatMessage({
                  id: "match.docs.uploadFailed",
                  defaultMessage: "Upload fehlgeschlagen.",
                })}
            </span>
          ) : kb < 1024 ? (
            `${kb} KB`
          ) : (
            `${(kb / 1024).toFixed(1)} MB`
          )}
        </div>
        {isError && row.onRetry && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              row.onRetry?.();
            }}
            className="mt-1 whitespace-nowrap text-smycles-micro tracking-[0.25em] text-ink-muted hover:text-ink"
          >
            <FormattedMessage id="match.docs.retry" defaultMessage="↻ Erneut versuchen" />
          </button>
        )}
      </div>
    </div>
  );
}

function RequirementsPanel({
  data,
  update,
  readOnly,
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly: boolean;
}) {
  const intl = useIntl();
  // Team-size is Optimize-only (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4): Assess/Rank evaluate a fixed candidate pool and have
  // no team to size. resolveMatchMode maps a legacy null to Optimize (unchanged) and an unknown
  // token to UNKNOWN, which shows no stepper — the negative form this replaced showed one.
  const teamControls = showsTeamControls(resolveMatchMode(data.matchMode));
  return (
    <div className="space-y-3">
      {teamControls && (
        <div className="bg-page box-flat flex items-center justify-between gap-3 px-5 py-2.5">
          <span className="text-smycles-body font-medium">
            <FormattedMessage id="match.teamSizeSection" defaultMessage="Größe des Teams" />
          </span>
          <NumberStepper
            id="match-team-size"
            value={data.teamSize}
            // Round + bound here rather than inside NumberStepper: the stepper accepts decimals on
            // purpose (RecruitingExperience uses step={0.5}). An unrounded value here is unreadable —
            // design-codec types teamSize as z.number().int() and parseDesign THROWS on a mismatch, so
            // "2,5" made the match editor unopenable.
            onChange={(next) => update({ teamSize: Math.round(next) })}
            min={1}
            max={TEAM_SIZE_MAX}
            disabled={readOnly}
            ariaLabel={intl.formatMessage({ id: "match.teamSizeSection", defaultMessage: "Größe des Teams" })}
          />
        </div>
      )}

      <TypedRequirementsPanel data={data} update={update} readOnly={readOnly} />
    </div>
  );
}

function TeamPowersPanel({
  data,
  update,
  readOnly,
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly: boolean;
}) {
  const intl = useIntl();

  const showPowerScoreSettings = usesPowerScoreSettings(resolveMatchMode(data.matchMode));
  const [basisOpen, setBasisOpen] = useState(false);

  return (
    <Disclosure title={intl.formatMessage(data.matchMode === "RANK"
      ? { id: "match.teamFit.roleScorecardLabel" }
      : { id: "match.teamPowers", defaultMessage: "Power Scorecard" })} defaultOpen>
      <PowersPicker
        profile={data.teamProfile}
        onChange={(profile) => update({ teamProfile: profile })}
        readOnly={readOnly}
      />
      {/* EIN BENANNTER AUSLOESER, KEIN ZWEITER KASTEN UND KEIN CHEVRON-ABSCHNITT. Die Karte
          gehoert der Scorecard; diese beiden Regler sind Expertensache und brauchen im Ruhezustand
          eine Zeile, nicht zwei. Ausgeklappt steht genau die Ansicht da, die vorher dauerhaft
          stand.

          Der Wert bleibt aus der eingeklappten Zeile draussen: eine Zusammenfassung waere eine
          zweite Darstellung derselben zwei Werte, und das Ergebnis benennt die Linse ohnehin
          (ResultStep). Ein Tooltip fuer beide zusammen statt einer je Regler — die Optionen
          sprechen fuer sich, erklaerungsbeduerftig ist nur, warum es die Wahl gibt.

          Die eigentliche Zurueckhaltung passiert weiterhin eine Ebene hoeher:
          usesPowerScoreSettings blendet beides in jedem Modus aus, der es nicht liest. */}
      {showPowerScoreSettings ? (
        <div className="mt-5">
          <div className="flex items-center gap-1">
            <button
              type="button"
              id="match-data-basis-toggle"
              aria-expanded={basisOpen}
              onClick={() => setBasisOpen((open) => !open)}
              className="smycles-field-label underline-offset-2 transition hover:text-ink hover:underline"
            >
              <FormattedMessage id="match.powerScore.edit" defaultMessage="Datengrundlage anpassen" />
            </button>
            <InfoTooltip
              ariaLabel={intl.formatMessage({
                id: "match.powerScore.editInfo",
                defaultMessage: "Datengrundlage erklären",
              })}
              contentClassName="w-[20rem] max-w-[calc(100vw-2rem)]"
            >
              <FormattedMessage
                id="match.powerScore.editHint"
                defaultMessage="Nicht jede Person hat beide Erhebungen, und nicht zu jeder Power einen Wert. Diese beiden Einstellungen entscheiden, welche Werte der Lauf benutzt und wie fehlende zählen."
              />
            </InfoTooltip>
          </div>
          <Collapse open={basisOpen}>
            <div className="pt-4">
              <PowerScoreSettings data={data} update={update} readOnly={readOnly} />
            </div>
          </Collapse>
        </div>
      ) : null}
    </Disclosure>
  );
}


function PowersPicker({
  profile,
  onChange,
  readOnly,
}: {
  profile: MatchData["teamProfile"];
  onChange: (profile: MatchData["teamProfile"]) => void;
  readOnly: boolean;
}) {
  const intl = useIntl();
  const { data, loading, error } = useQuery(MatchTeamProfilesDocument, {
    variables: { input: { first: 50 } as any },
  });
  const profiles = useMemo(() => {
    return ((data as any)?.teamProfiles?.nodes ?? []).map((p: any) => {
      const enumWeights: Record<string, number> = {};
      for (const { element, weight } of p.weights ?? []) {
        if (element) {
          enumWeights[element] = normalizePowersWeight(weight);
        }
      }
      return {
        id: p.id as string,
        name: (p.name ?? p.id) as string,
        label:
          p.localizedName?.localized ||
          p.localizedName?.english ||
          p.name ||
          p.id,
        // Densify at the picker seam so downstream consumers (preset apply,
        // Individuell switch, reset, divergence check) never see a sparse
        // preset. Missing keys default to 0 (preset author intent).
        // Without this, switching to Individuell would carry the sparse map
        // forward, then dataToDesign would fill the gaps with 5 — inflating
        // previously-excluded Powers and silently changing the brief.
        elementWeights: densifyElementWeights(enumWeights, POWERS_WEIGHT_MIN),
      };
    });
  }, [data]);

  // Designs saved before genesis#211 reference the old client-side synthetic
  // "Ausgewogen"; the seeded row is the same profile, so map the id onto it and
  // the dropdown keeps showing a selection for those matches.
  const savedProfileId = profile?.id ?? DEFAULT_TEAM_PROFILE_ID;
  const selectedProfileId =
    savedProfileId === LEGACY_BALANCED_TEAM_PROFILE_ID
      ? BALANCED_TEAM_PROFILE_SEED_ID
      : savedProfileId;

  // A design can reference a profile the list no longer carries: admins can now
  // delete Power Profiles (spark#218), and the list is still loading on first
  // paint. Carry the stored one as its own option under the name saved in the
  // design, otherwise the Select falls back to rendering the first option and
  // the brief silently reads "Individuell" while still holding those weights.
  const pickerProfiles = useMemo(() => {
    if (!profile || selectedProfileId === DEFAULT_TEAM_PROFILE_ID) return profiles;
    if (profiles.some((p: any) => p.id === selectedProfileId)) return profiles;
    const label = profile.name || selectedProfileId;
    return [...profiles, { id: selectedProfileId, name: profile.name, label, elementWeights: profile.elementWeights }];
  }, [profiles, profile, selectedProfileId]);

  // Divergence: is the live profile's Importance shape identical to the source
  // preset's saved shape? If not, the dropdown label gets a "· geändert"
  // suffix so the user knows they've drifted. Reselecting the preset replaces
  // the weights — the dropdown is its own reset path. See @adr SPARK-0033-power-weights-word-scale-plus-exact-field@1.
  const sourcePreset = profile
    ? pickerProfiles.find((p: any) => p.id === selectedProfileId)
    : null;
  const isCustom = profile?.id === DEFAULT_TEAM_PROFILE_ID;
  const isModified = useMemo(() => {
    if (!profile || !sourcePreset) return false;
    const a = profile.elementWeights;
    const b = sourcePreset.elementWeights as Record<string, number>;
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
      if ((a[k] ?? 0) !== (b[k] ?? 0)) return true;
    }
    return false;
  }, [profile, sourcePreset]);

  function setProfile(id: string) {
    if (id === DEFAULT_TEAM_PROFILE_ID) {
      // Switching to Individuell keeps the current weights – picking it is the
      // user saying "I'm no longer following a preset", not "reset my shape".
      // The Zurücksetzen button is the explicit reset path.
      onChange(makeIndividualTeamProfile(profile?.elementWeights));
      return;
    }
    const sel = pickerProfiles.find((p: any) => p.id === id);
    if (!sel) return;
    onChange({
      id: sel.id,
      name: sel.name,
      elementWeights: { ...sel.elementWeights },
    });
  }

  function resetToPreset() {
    if (!profile || !sourcePreset) return;
    onChange({
      id: sourcePreset.id,
      name: sourcePreset.name,
      elementWeights: { ...(sourcePreset.elementWeights as Record<string, number>) },
    });
  }

  const canReset = !readOnly && isModified && !isCustom && Boolean(sourcePreset);

  const modifiedSuffix = intl.formatMessage({
    id: "match.teamProfileModifiedSuffix",
    defaultMessage: " · geändert",
  });
  const customLabel = intl.formatMessage({
    id: "match.teamProfileCustom",
    defaultMessage: "Individuell",
  });

  return (
    <div className="space-y-5">
      <div>
        <SubsectionHeader
          title={intl.formatMessage({ id: "match.teamProfile", defaultMessage: "Selection" })}
        />
        <Select
          id="match-team-profile"
          value={selectedProfileId}
          onChange={(next) => setProfile(next)}
          disabled={loading || readOnly}
          hideEmpty
          options={[
            { value: DEFAULT_TEAM_PROFILE_ID, label: customLabel },
            ...pickerProfiles.map((p: any) => ({
              value: p.id,
              label: isModified && selectedProfileId === p.id ? `${p.label}${modifiedSuffix}` : p.label,
            })),
          ]}
        />
        {error && (
          <div className="mt-1 text-smycles-label text-red-600">
            <FormattedMessage id="match.profilesLoadError" defaultMessage="Profile laden: {message}" values={{ message: userErrorMessage(intl, error) }} />
          </div>
        )}
      </div>

      {profile ? <PowerProfileEditor profile={profile} onChange={onChange} readOnly={readOnly} /> : null}

      {canReset ? (
        <div className="flex items-center justify-end">
          <button
            onClick={resetToPreset}
            className="text-smycles-label tracking-widest text-ink-muted underline-offset-2 hover:text-ink hover:underline"
          >
            <FormattedMessage id="match.editor.reset" defaultMessage="Zurücksetzen" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
